import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import LogoutIcon from '@mui/icons-material/Logout';
import { leaveCoordinatorChat } from '../../api/admin';
import { getTeam, listChatMessages, postChatMessage } from '../../api/runs';
import { useAuth } from '../../auth/useAuth';
import type { ChatMessage, Team } from '../../api/types';
import { useWebSocket } from '../../hooks/useWebSocket';
import ChatMessageList from '../../components/chat/ChatMessageList';
import ChatComposer from '../../components/chat/ChatComposer';
import ErrorAlert from '../../components/ErrorAlert';

/**
 * Coordinator-side team chat (issue #238).
 *
 * Coordinators reach this page from the run dashboard (chat icon on a team
 * row, or the "Hilfe" chip of a requesting team). It shows the team's full
 * chat history, receives new messages live over the same WebSocket topic the
 * participants use, and lets the coordinator reply into the team's chat.
 */
const CoordinatorTeamChat: React.FC = () => {
  const { runId, teamId } = useParams<{ runId: string; teamId: string }>();
  const { user } = useAuth();
  const { t } = useTranslation('runs');
  const navigate = useNavigate();

  const orgSlug = user?.organization_slug;

  const [team, setTeam] = useState<Team | null>(null);
  const [teamError, setTeamError] = useState<string | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [deliveryFailed, setDeliveryFailed] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const [leaveFailed, setLeaveFailed] = useState(false);

  const load = useCallback(async () => {
    if (!orgSlug || !runId || !teamId) return;
    try {
      const teamData = await getTeam(orgSlug, runId, teamId);
      setTeam(teamData);
      setTeamError(null);
    } catch {
      setTeamError(t('teamChat.loadFailed'));
    }
    try {
      const resp = await listChatMessages(orgSlug, runId, teamId);
      // The endpoint orders by created_at ASC — oldest first, which is already
      // the render order. Reversing it here put the newest message at the top.
      setMessages(resp.items);
    } catch {
      // Initial load failure stays silent in the UI; the WS will retry.
    }
  }, [orgSlug, runId, teamId, t]);

  useEffect(() => {
    void load();
  }, [load]);

  // Same topic the participant TeamChat subscribes to — ws.py authorizes
  // coordinator+ only while the team's assist window is open (spec 037).
  useWebSocket({
    path:
      orgSlug && runId && teamId
        ? `/api/orgs/${orgSlug}/runs/${runId}/teams/${teamId}/ws`
        : '',
    enabled: !!(orgSlug && runId && teamId),
    handlers: {
      chat_message: (data) => {
        const msg = data as ChatMessage;
        setMessages((prev) => {
          if (prev.some((m) => m.id === msg.id)) return prev;
          return [...prev, msg];
        });
      },
    },
  });

  const canSend = !!draft.trim() && !sending && !!(orgSlug && runId && teamId);

  const handleSend = async () => {
    if (!canSend) return;
    setSending(true);
    setDeliveryFailed(false);
    try {
      await postChatMessage(orgSlug!, runId!, teamId!, { content: draft.trim() });
      setDraft('');
      // Message will arrive via WS; if it doesn't (slow link), the next
      // visit will pick it up on reload.
    } catch {
      // Keep the draft so the coordinator can retry without retyping.
      setDeliveryFailed(true);
    } finally {
      setSending(false);
    }
  };

  // Spec 037: leaving closes the team's coordinator assist window (SOW
  // §8.4.2 "jederzeit verlassen") — access ends until the team requests
  // assistance again.
  const handleLeave = async () => {
    if (!orgSlug || !runId || !teamId || leaving) return;
    setLeaving(true);
    setLeaveFailed(false);
    try {
      await leaveCoordinatorChat(orgSlug, runId, teamId);
      navigate(`/runs/${runId}`);
    } catch {
      setLeaveFailed(true);
    } finally {
      setLeaving(false);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100dvh - 220px)',
      }}
    >
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 1 }}>
        <IconButton
          aria-label={t('teamChat.back')}
          onClick={() => navigate(`/runs/${runId}`)}
          size="small"
        >
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h2" component="h1" sx={{ flexGrow: 1 }}>
          {t('teamChat.title', { teamName: team?.name ?? '…' })}
        </Typography>
        <Button
          size="small"
          color="inherit"
          startIcon={<LogoutIcon fontSize="small" />}
          onClick={() => void handleLeave()}
          disabled={leaving}
        >
          {t('teamChat.leave')}
        </Button>
      </Stack>

      {leaveFailed && (
        <Box sx={{ mb: 1 }}>
          <ErrorAlert message={t('teamChat.leaveFailed')} />
        </Box>
      )}

      {teamError && (
        <Box sx={{ mb: 1 }}>
          <ErrorAlert message={teamError} />
        </Box>
      )}

      {deliveryFailed && (
        <Alert
          severity="error"
          sx={{ mb: 1 }}
          action={
            <Stack direction="row" spacing={0.5}>
              <Button
                color="inherit"
                size="small"
                onClick={() => void handleSend()}
                disabled={sending}
              >
                {t('participant.chat.deliveryFailedRetry')}
              </Button>
              <IconButton
                color="inherit"
                size="small"
                onClick={() => {
                  setDeliveryFailed(false);
                  setDraft('');
                }}
                aria-label={t('participant.chat.deliveryFailedDismiss')}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Stack>
          }
        >
          {t('participant.chat.deliveryFailed')}
        </Alert>
      )}

      <ChatMessageList
        messages={messages}
        currentUserId={user?.id}
        emptySubtitle={t('teamChat.emptySubtitle')}
      />

      <ChatComposer
        draft={draft}
        onDraftChange={setDraft}
        onSend={() => void handleSend()}
        canSend={canSend}
      />
    </Box>
  );
};

export default CoordinatorTeamChat;
